import { useMemo } from 'react';
import { Card } from '@/components/ui/Card';
import { Clock, Calendar, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import type { Task } from '@/types';
import { formatDate, formatDateRelative } from '@/lib/utils';

interface DeliveryTimelineProps {
  tasks: Task[];
}

export function DeliveryTimeline({ tasks }: DeliveryTimelineProps) {
  const timelineItems = useMemo(() => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];

    const todayItems = tasks
      .filter(t => new Date(t.estimatedCompletion).toISOString().split('T')[0] === todayStr)
      .map(t => ({ ...t, timeLabel: 'today' as const }));

    const nextItems = tasks
      .filter(t => {
        const completion = new Date(t.estimatedCompletion);
        const diff = (completion.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
        return diff > 0 && diff <= 7 && t.status !== 'approved';
      })
      .slice(0, 3)
      .map(t => ({ ...t, timeLabel: 'next' as const }));

    const pendingItems = tasks
      .filter(t => t.status === 'review' || t.status === 'delivered')
      .map(t => ({ ...t, timeLabel: 'pending' as const }));

    return { today: todayItems, next: nextItems, pending: pendingItems };
  }, [tasks]);

  return (
    <Card title="Delivery Timeline" subtitle="What is happening now and what is coming up">
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Clock className="w-4 h-4 text-blue-600" />
            </div>
            <h4 className="font-semibold text-slate-900">Being Worked On Today</h4>
            <span className="ml-auto text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
              {timelineItems.today.length} tasks
            </span>
          </div>
          {timelineItems.today.length === 0 ? (
            <div className="p-4 bg-slate-50 rounded-lg text-sm text-slate-500 text-center">
              No tasks scheduled for today
            </div>
          ) : (
            <div className="space-y-2">
              {timelineItems.today.map((task) => (
                <div
                  key={task.id}
                  className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100"
                >
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900 text-sm">{task.title}</p>
                    <p className="text-xs text-slate-500">
                      {task.assignedDeveloper?.name || 'Unassigned'}
                    </p>
                  </div>
                  <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded">
                    In Progress
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
              <Calendar className="w-4 h-4 text-purple-600" />
            </div>
            <h4 className="font-semibold text-slate-900">Next Up (This Week)</h4>
            <span className="ml-auto text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
              {timelineItems.next.length} tasks
            </span>
          </div>
          {timelineItems.next.length === 0 ? (
            <div className="p-4 bg-slate-50 rounded-lg text-sm text-slate-500 text-center">
              No upcoming tasks this week
            </div>
          ) : (
            <div className="space-y-2">
              {timelineItems.next.map((task) => (
                <div
                  key={task.id}
                  className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-100"
                >
                  <ArrowRight className="w-4 h-4 text-purple-400" />
                  <div className="flex-1">
                    <p className="font-medium text-slate-900 text-sm">{task.title}</p>
                    <p className="text-xs text-slate-500">
                      Due {formatDateRelative(new Date(task.estimatedCompletion))}
                    </p>
                  </div>
                  <span className="text-xs text-slate-500">
                    {formatDate(new Date(task.estimatedCompletion))}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
              <AlertCircle className="w-4 h-4 text-amber-600" />
            </div>
            <h4 className="font-semibold text-slate-900">Pending Approval</h4>
            <span className="ml-auto text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
              {timelineItems.pending.length} items
            </span>
          </div>
          {timelineItems.pending.length === 0 ? (
            <div className="p-4 bg-slate-50 rounded-lg text-sm text-slate-500 text-center">
              No items pending approval
            </div>
          ) : (
            <div className="space-y-2">
              {timelineItems.pending.map((task) => (
                <div
                  key={task.id}
                  className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100"
                >
                  <CheckCircle className="w-4 h-4 text-amber-500" />
                  <div className="flex-1">
                    <p className="font-medium text-slate-900 text-sm">{task.title}</p>
                    <p className="text-xs text-slate-500">
                      {task.status === 'review' ? 'Ready for Review' : 'Delivered - Awaiting Approval'}
                    </p>
                  </div>
                  <button className="text-xs font-medium text-amber-700 hover:text-amber-800 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded transition-colors">
                    Review
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
